import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FamilyLinkType, ProfileField } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { BcitiConfirmDialogComponent } from 'src/app/global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { InformationalDialogComponent, ErrorSeverity } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { PostNewFamilyLink, Profile } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';
import { formatDateOnly } from 'src/app/global/utils/date/date.utils';
import { organisationTagSelector } from 'src/app/global/store/global.selectors';
import { userRecordSelector } from '../../store/user.selectors';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';

interface SelectOption {
    id: number;
    name: string;
    value: any;
}

const LINK_CHILD = 'CHILD';
const LINK_PARENT = 'PARENT';
const LINK_OTHER = 'OTHER';
const LINK_SPOUSE = 'SPOUSE';

@Component({
    selector: 'add-new-family-member-dialog',
    templateUrl: './add-new-family-member-dialog.component.html',
    styleUrls: ['./add-new-family-member-dialog.component.scss'],
    providers: [DatePipe],
})
export class AddNewFamilyMemberDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    private _isVisible = true;

    public readOnly: boolean = false;

    profileField = ProfileField;

    hiddenFields: ProfileField[] = [];

    // TODO turn into config
    isBrossard = false;

    viewMode: string = 'edit';

    formLoading = true;

    allCoreConfig: any;

    profile: Profile;

    formattedAddress: string;

    processingSubmit = false;

    maxDate = new Date();

    selectedLink: {};

    selectedGender: {};

    selectedAvatar: File;

    get genderOptions(): SelectOption[] {
        const options: SelectOption[] = [
            {
                id: 1,
                value: 'female',
                name: this.translate.instant('pages.user-cards.family.create.gender.female'),
            },
            {
                id: 2,
                value: 'male',
                name: this.translate.instant('pages.user-cards.family.create.gender.male'),
            },
            {
                id: 3,
                value: 'other',
                name: this.translate.instant('pages.user-cards.family.create.gender.other'),
            },
        ];

        return this.isBrossard ? options.filter((option) => option.value !== 'other') : options;
    }

    familyLinks: SelectOption[] = Object.values(FamilyLinkType).map((link, index) => ({
        id: index,
        name: this.translate.instant(`pages.user-cards.family.create.link.${link.toLowerCase()}`),
        value: link,
    }));

    addFamilyMemberForm: any = new FormGroup({
        firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        birthDate: new FormControl('', [Validators.required]),
        address: new FormControl('', [Validators.required]),
        phone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
        gender: new FormControl('', [Validators.required]),
        avatar: new FormControl('', [Validators.required]),
        targetLink: new FormControl('', [Validators.required]),
        sourceLink: new FormControl('', [Validators.required]),
    });

    get firstName() {
        return this.addFamilyMemberForm.get('firstName');
    }
    get lastName() {
        return this.addFamilyMemberForm.get('lastName');
    }
    get email() {
        return this.addFamilyMemberForm.get('email');
    }
    get gender() {
        return this.addFamilyMemberForm.get('gender');
    }
    get link() {
        return this.addFamilyMemberForm.get('link');
    }
    get address() {
        return this.addFamilyMemberForm.get('address');
    }
    get birthDate() {
        return this.addFamilyMemberForm.get('birthDate');
    }
    get phone() {
        return this.addFamilyMemberForm.get('phone');
    }
    get targetLink() {
        return this.addFamilyMemberForm.get('targetLink');
    }
    get sourceLink() {
        return this.addFamilyMemberForm.get('sourceLink');
    }
    get avatar() {
        return this.addFamilyMemberForm.get('avatar');
    }

    constructor(
        private translate: TranslateService,
        private dialogRef: MatDialogRef<AddNewFamilyMemberDialogComponent>,
        private dialog: MatDialog,
        private userService: UserService,
        private messageService: MessageService,
        private datePipe: DatePipe,
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) data,
    ) {
        this.dialogRef.disableClose = true;

        this.dialogRef
            .backdropClick()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.closeDialog();
            });
    }

    get isVisible(): boolean {
        return this._isVisible;
    }

    set isVisible(v: boolean) {
        this._isVisible = v;
    }

    get phoneLabel(): string {
        if (this.isBrossard) {
            return this.translate.instant('pages.user-cards.family.create.phone.label');
        }
        return this.translate.instant('pages.user-cards.family.create.phone.label-optional');
    }

    ngOnInit(): void {
        this.formLoading = true;

        this.store
            .select(userRecordSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((profile) => {
                    this.profile = profile;
                    this.addFamilyMemberForm.addControl('email', new FormControl('', [Validators.email, this.forbiddenSameEmailValidator(this.profile.email)]));

                    if (this.profile.phone) {
                        this.addFamilyMemberForm.patchValue({ phone: this.profile.phone });
                    }

                    const primaryAddress = this.profile.addresses.find((address) => address.isPrimary === true) ?? this.profile.addresses[0];

                    if (primaryAddress) {
                        this.addFamilyMemberForm.patchValue({ address: primaryAddress });
                    }

                    this.formLoading = false;
                }),
            )
            .subscribe();

        // TODO: Turn into config
        // make phone number required for brossard org
        this.store
            .select(organisationTagSelector)
            .pipe(
                take(1),
                map((tag) => {
                    if (tag === 'BROSSARD') {
                        this.isBrossard = true;
                        this.addFamilyMemberForm.controls['phone'].setValidators([Validators.required, Validators.minLength(14), Validators.maxLength(14)]);
                        this.addFamilyMemberForm.controls['phone'].updateValueAndValidity();
                    }
                }),
            )
            .subscribe();
    }

    closeDialog() {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            maxWidth: '580px',
            panelClass: 'confirm-dialog',
            data: {
                dialog: {
                    title: this.translate.instant('pages.user-cards.family.create.confirm-dialog.title'),
                    body: this.translate.instant('pages.user-cards.family.create.confirm-dialog.body'),
                    icon: '',
                    iconColor: '',
                    confirmLabel: this.translate.instant('pages.user-cards.common.button.close'),
                    cancelLabel: this.translate.instant('pages.user-cards.common.button.cancel'),
                    confirmButtonClass: 'bciti-button--red',
                    cancelButtonClass: 'bciti-button bciti-button--lightblue-inversed',
                },
                component: {},
            },
        });

        dialogRef.componentInstance.onSubmit.subscribe((e) => {
            dialogRef.close();
            if (e) {
                this.dialogRef.close();
            }
        });
    }

    toggleDialogVisibility(): void {
        this.isVisible = !this.isVisible;
    }

    avatarChange(file: string) {
        this.addFamilyMemberForm.get('avatar').markAsTouched();
    }

    genderChange(option) {
        this.selectedGender = option;
        this.addFamilyMemberForm.patchValue({ gender: option.value });
        this.addFamilyMemberForm.get('gender').markAsTouched();
    }

    linkChange(event: SelectOption) {
        this.selectedLink = event;
        this.addFamilyMemberForm.patchValue({ targetLink: event.value });
        this.addFamilyMemberForm.get('targetLink').markAsTouched();

        switch (event.value.toUpperCase()) {
            case LINK_CHILD:
                this.addFamilyMemberForm.patchValue({ sourceLink: LINK_PARENT });
                break;
            case LINK_PARENT:
                this.addFamilyMemberForm.patchValue({ sourceLink: LINK_CHILD });
                break;
            case LINK_OTHER:
                this.addFamilyMemberForm.patchValue({ sourceLink: LINK_OTHER });
                break;
            case LINK_SPOUSE:
                this.addFamilyMemberForm.patchValue({ sourceLink: LINK_SPOUSE });
                break;
        }

        this.addFamilyMemberForm.get('sourceLink').markAsTouched();
    }

    addressChange(address: string) {
        this.formattedAddress = address;
    }

    switchReviewMode() {
        this.addFamilyMemberForm.markAllAsTouched();
        this.scrollToFirstInvalidControl();

        if (this.addFamilyMemberForm.valid) {
            this.viewMode = 'review';
        }
    }

    private scrollToFirstInvalidControl() {
        setTimeout(() => {
            let form = document.getElementById('idAddFamilyMemberForm');
            let firstInvalidControl = form.querySelector('.ng-invalid');
            if (firstInvalidControl) {
                firstInvalidControl.scrollIntoView({ behavior: 'smooth' });
                (firstInvalidControl as HTMLElement).focus();
            }
        }, 300);
    }

    switchEditMode() {
        this.viewMode = 'edit';
    }

    submit() {
        if (this.addFamilyMemberForm.invalid) return;

        this.processingSubmit = true;

        const formValue = this.addFamilyMemberForm.value;

        const formattedFormValue = {
            ...formValue,
            firstName: formValue.firstName.trim(),
            lastName: formValue.lastName.trim(),
            birthDate: formatDateOnly(formValue.birthDate),
            picture: formValue.avatar ? formValue.avatar : '',
            phone: formValue.phone !== '' ? formValue.phone : null,
            email: formValue.email !== '' ? formValue.email : null,
            sex: formValue.gender,
            isPrimary: true,
            proofOfIdentity: [],
            name: '',
        };

        delete formattedFormValue.avatar;
        delete formattedFormValue.gender;

        const payload: PostNewFamilyLink = formattedFormValue;

        this.store
            .select(userRecordSelector)
            .pipe(
                take(1),
                switchMap((userRecord) => {
                    if (payload.email) {
                        return this.userService
                            .registerFamilyZitadelUser({
                                email: payload.email,
                                firstName: payload.firstName ?? '',
                                lastName: payload.lastName ?? '',
                            })
                            .pipe(
                                catchError((err) => (err?.status === 409 ? of(null) : throwError(() => err))),
                                switchMap(() => this.userService.postNewFamilyLink(userRecord.id, payload)),
                            );
                    } else {
                        return this.userService.postNewFamilyLink(userRecord.id, payload);
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    this.processingSubmit = false;
                    this.messageService.success(this.translate.instant('pages.user-cards.family.create.messages.success'));
                },
                error: (error) => {
                    this.processingSubmit = false;
                    this.handleSubmitError(error);
                },
            });
    }

    private handleSubmitError(error: any): void {
        if (error?.status === 400 || error?.status === 404) {
            this.dialogRef.addPanelClass('is-hidden');
            this.dialog
                .open(InformationalDialogComponent, {
                    panelClass: 'bciti-error-dialog',
                    data: {
                        title: this.translate.instant('pages.user-cards.family.create.messages.error'),
                        body: error?.error?.messages[0],
                        errorSeverity: ErrorSeverity.Error,
                    },
                })
                .afterClosed()
                .subscribe(() => {
                    this.dialogRef.removePanelClass('is-hidden');
                });
        } else {
            this.messageService.error(this.translate.instant('pages.user-cards.family.create.messages.error'));
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private forbiddenSameEmailValidator(email: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const forbidden = email == control.value;
            return forbidden ? { sameEmail: 'error' } : null;
        };
    }

    displayField(hiddenField: ProfileField): boolean {
        return !this.hiddenFields.includes(hiddenField);
    }
}
